/**
 * Utility functions for reliable calendar date and time formatting
 * adhering strictly to Central Time (America/Chicago) and preventing UTC midnight rollback bugs.
 */

export function formatCalendarDate(
    dateStr: string | Date | null | undefined,
    options?: Intl.DateTimeFormatOptions
): string {
    if (!dateStr) return '';

    if (dateStr instanceof Date) {
        if (isNaN(dateStr.getTime())) return '';
        return dateStr.toLocaleDateString('en-US', options || {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    }

    const str = String(dateStr).trim();
    if (!str) return '';

    // If string is already in full human format (e.g. "Saturday, September 20, 2026")
    if (str.includes(',') && isNaN(Number(str[0]))) {
        return str;
    }

    // If it's a date-only "YYYY-MM-DD" or contains "YYYY-MM-DD"
    const cleanDate = str.split('T')[0].split(' ')[0];
    const parts = cleanDate.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
        const [year, month, day] = parts.map(Number);
        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
            // Local date constructor (year, monthIndex, day) creates date in calendar without UTC midnight shift
            const localDate = new Date(year, month - 1, day);
            return localDate.toLocaleDateString('en-US', options || {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        }
    }

    const d = new Date(str);
    return isNaN(d.getTime()) ? str : d.toLocaleDateString('en-US', options || {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

/**
 * Formats time range (e.g., "01:00:00" -> "1:00 AM", or "01:00" - "03:00" -> "1:00 AM - 3:00 AM CST")
 */
export function formatTimeRange(
    startTime: string | null | undefined,
    endTime?: string | null | undefined
): string {
    if (!startTime) return '';

    const formatSingle = (t: string) => {
        if (!t) return '';
        const clean = t.split(':');
        if (clean.length < 2) return t;
        let h = parseInt(clean[0], 10);
        const m = clean[1];
        const ampm = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;
        return `${h}:${m} ${ampm}`;
    };

    const startFmt = formatSingle(startTime);
    if (!endTime) return startFmt;
    const endFmt = formatSingle(endTime);
    return `${startFmt} - ${endFmt} CST`;
}
